import cn from 'classnames';
import { ReactNode, memo, useState } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { MdDelete, MdEdit, MdMoreVert } from 'react-icons/md';
import { useSelector } from 'react-redux';
import { ActivityComment } from '@blisspointmedia/bpm-types/dist/OracleActivity';
import { AuthorAvatar, authorDisplayLabel, isCommentAuthoredBy } from '@Components/AuthorAvatar';
import { Button } from '@Components/Button';
import { Appearance, Design, Intent, Size } from '@Components/designConstants';
import { IconButton } from '@Components/IconButton';
import { useSetAreYouSure, useSetError } from '@src/redux/modals';
import { emailSelector } from '@src/redux/user';
import { Brand60 } from '@src/utils/colors';
import { formatFriendlyTimestamp } from '@src/utils/date-utils';
import { COMMENT_BODY_MAX, GHOST_ICON_BUTTON_PROPS } from '../activity-feed-constants';
import { ActivityId } from '../activity-feed-data-utils';
import { useDeleteEventComment, usePatchEventComment } from '../useOracleEventMutations';
import { CommentBody } from './CommentBody';
import { CommentEditor } from './CommentEditor';
import { useCommentDraft } from './useCommentDraft';

interface CommentListProps {
  activityId: ActivityId;
  comments: ActivityComment[];
}

export const CommentList = ({ activityId, comments }: CommentListProps): ReactNode => {
  if (comments.length === 0) return null;
  return (
    <ul className='activity-feed-detail-comment-list'>
      {comments.map(comment => (
        <CommentRow key={comment.id} activityId={activityId} comment={comment} />
      ))}
    </ul>
  );
};

interface CommentRowProps {
  activityId: ActivityId;
  comment: ActivityComment;
}

const CommentRow = memo(({ activityId, comment }: CommentRowProps): ReactNode => {
  const [isEditing, setIsEditing] = useState(false);
  const currentUserEmail = useSelector(emailSelector);
  const isAuthor = isCommentAuthoredBy(comment.authorEmail, currentUserEmail);
  const isDeleted = Boolean(comment.deletedAt);
  const isEdited = Boolean(comment.updatedAt) && !isDeleted;
  const label = authorDisplayLabel(comment.displayName, comment.authorEmail);

  return (
    <li className={cn('activity-feed-detail-comment', { deleted: isDeleted })}>
      <AuthorAvatar displayName={comment.displayName} email={comment.authorEmail} />
      <div className='activity-feed-detail-comment-content'>
        <div className='activity-feed-detail-comment-header'>
          <div className='activity-feed-detail-comment-meta'>
            <span className='activity-feed-detail-comment-author'>{label}</span>
            <span className='activity-feed-detail-comment-meta-divider' aria-hidden='true'>
              •
            </span>
            <span className='activity-feed-detail-comment-timestamp'>
              {formatFriendlyTimestamp(comment.createdAt)}
            </span>
            {isEdited && <span className='activity-feed-detail-comment-edited-flag'>(edited)</span>}
          </div>
          {isAuthor && !isDeleted && !isEditing && (
            <CommentKebabMenu
              activityId={activityId}
              comment={comment}
              onStartEdit={() => setIsEditing(true)}
            />
          )}
        </div>
        {isDeleted && <CommentTombstone displayLabel={label} />}
        {!isDeleted && !isEditing && <CommentBody markdown={comment.body} />}
        {!isDeleted && isEditing && (
          <CommentInlineEditor
            activityId={activityId}
            comment={comment}
            onDone={() => setIsEditing(false)}
          />
        )}
      </div>
    </li>
  );
});

interface CommentTombstoneProps {
  displayLabel: string;
}

const CommentTombstone = ({ displayLabel }: CommentTombstoneProps): ReactNode => (
  <div className='activity-feed-detail-comment-body markdown-body'>
    <p>Deleted note by {displayLabel}</p>
  </div>
);

interface CommentKebabMenuProps {
  activityId: ActivityId;
  comment: ActivityComment;
  onStartEdit: () => void;
}

const CommentKebabMenu = ({
  activityId,
  comment,
  onStartEdit
}: CommentKebabMenuProps): ReactNode => {
  const [isOpen, setIsOpen] = useState(false);
  const setError = useSetError();
  const setAreYouSure = useSetAreYouSure();
  const { mutateAsync: deleteComment } = useDeleteEventComment();

  // react-bootstrap's controlled Overlay doesn't auto-close on item click.
  const runAndClose = (action: () => void) => () => {
    setIsOpen(false);
    action();
  };

  const handleDelete = async () => {
    try {
      await setAreYouSure({
        title: 'Delete note?',
        message: 'This note will be deleted',
        okayText: 'Delete',
        cancelText: 'Cancel',
        intent: Intent.ERROR
      });
    } catch {
      // User cancelled; setAreYouSure rejects on Cancel by convention.
      return;
    }
    try {
      await deleteComment({ activityId, comment });
    } catch (e) {
      setError({ message: `Couldn't delete note: ${e.message}`, reportError: e });
    }
  };

  return (
    <OverlayTrigger
      show={isOpen}
      onToggle={setIsOpen}
      trigger={[]}
      placement='bottom-end'
      overlay={
        <Popover
          id={`activity-feed-comment-menu-${comment.id}`}
          className='activity-feed-detail-comment-menu-popover'
        >
          <div className='activity-feed-detail-comment-menu'>
            <Button
              appearance={Appearance.EMPTY}
              design={Design.NEUTRAL}
              leadingElement={<MdEdit color={Brand60} />}
              onClick={runAndClose(onStartEdit)}
              className='activity-feed-detail-comment-menu-item'
            >
              Edit
            </Button>
            <Button
              appearance={Appearance.EMPTY}
              design={Design.NEUTRAL}
              leadingElement={<MdDelete color={Brand60} />}
              onClick={runAndClose(handleDelete)}
              className='activity-feed-detail-comment-menu-item'
            >
              Delete
            </Button>
          </div>
        </Popover>
      }
    >
      <IconButton
        {...GHOST_ICON_BUTTON_PROPS}
        aria-label='Note actions'
        onClick={() => setIsOpen(open => !open)}
      >
        <MdMoreVert />
      </IconButton>
    </OverlayTrigger>
  );
};

interface CommentInlineEditorProps {
  activityId: ActivityId;
  comment: ActivityComment;
  onDone: () => void;
}

const CommentInlineEditor = ({
  activityId,
  comment,
  onDone
}: CommentInlineEditorProps): ReactNode => {
  const { mutateAsync: patchComment, isPending } = usePatchEventComment();
  const { draft, setDraft, trimmed, handleSubmit } = useCommentDraft(
    comment.body,
    async body => {
      await patchComment({ activityId, comment, body: { body } });
      onDone();
    },
    'save note'
  );
  const isUnchanged = trimmed === comment.body.trim();
  const canSave =
    !isUnchanged && trimmed.length > 0 && trimmed.length <= COMMENT_BODY_MAX && !isPending;
  const handleSave = () => {
    if (canSave) {
      handleSubmit();
    }
  };

  return (
    <div className='activity-feed-detail-comment-editor'>
      <CommentEditor
        markdown={draft}
        autoFocus
        canSubmit={canSave}
        onChange={setDraft}
        onSubmit={handleSave}
        onCancel={onDone}
      />
      <div className='activity-feed-detail-comment-editor-actions'>
        <Button
          size={Size.SM}
          design={Design.NEUTRAL}
          appearance={Appearance.OUTLINED}
          onClick={onDone}
        >
          Cancel
        </Button>
        <Button size={Size.SM} disabled={!canSave} loading={isPending} onClick={handleSave}>
          Save
        </Button>
      </div>
    </div>
  );
};
